import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { usePositionContext } from './PositionContext';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { useToast } from './ToastContext';
import { useTradingError, TradingError } from './TradingErrorContext';

interface OrderContextType {
  selectedPrice: number | null;
  setSelectedPrice: (price: number) => void;
  clearSelectedPrice: () => void;
  showOrderConfirmation: boolean;
  setShowOrderConfirmation: (show: boolean) => void;
  orderDetails: OrderDetails | null;
  setOrderDetails: (details: OrderDetails | null) => void;
  executeOrder: (details?: OrderDetails) => Promise<{ success: boolean; error?: string }>;
  showConfirmationPopup: boolean;
  setShowConfirmationPopup: (show: boolean) => void;
}

interface OrderDetails {
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop';
  symbol: string;
  price: number;
  stopPrice?: number;
  amount: number;
  leverage: number;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrderContext must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(() => {
    // Load from localStorage on initialization
    const saved = localStorage.getItem('showConfirmationPopup');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Get position context functions
  let addOpenPosition: ((position: any) => void) | null = null;
  let addPendingOrder: ((order: any) => void) | null = null;
  
  try {
    const positionContext = usePositionContext();
    addOpenPosition = positionContext.addOpenPosition;
    addPendingOrder = positionContext.addPendingOrder;
  } catch (error) {
    // PositionContext not available, will handle gracefully
    console.warn('PositionContext not available in OrderProvider');
  }

  // Get account context for balance updates
  let accountBalance: any = null;
  let updateBalance: ((type: 'demo' | 'live', amount: number) => void) | null = null;
  
  try {
    const accountContext = useAccount();
    accountBalance = accountContext.accountBalance;
    updateBalance = accountContext.updateBalance;
  } catch (error) {
    // AccountContext not available, will handle gracefully
    console.warn('AccountContext not available in OrderProvider');
  }

  // Get account type context
  let accountType: 'demo' | 'live' = 'demo';
  try {
    const accountTypeContext = useAccountType();
    accountType = accountTypeContext.accountType;
  } catch (error) {
    // AccountTypeContext not available, will handle gracefully
    console.warn('AccountTypeContext not available in OrderProvider');
  }

  // Get toast context
  let showToast: ((message: string, type: 'success' | 'error' | 'info' | 'warning' | 'buy' | 'sell') => void) | null = null;
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch (error) {
    // ToastContext not available, will handle gracefully
    console.warn('ToastContext not available in OrderProvider');
  }

  // Get trading error context
  let showTradingError: ((error: TradingError) => void) | null = null;
  let setOnRetry: ((callback: (() => void) | undefined) => void) | null = null;
  try {
    const tradingErrorContext = useTradingError();
    showTradingError = tradingErrorContext.showError;
    setOnRetry = tradingErrorContext.setOnRetry;
  } catch (error) {
    // TradingErrorContext not available, will handle gracefully
    console.warn('TradingErrorContext not available in OrderProvider');
  }

  // Save to localStorage whenever the setting changes
  useEffect(() => {
    localStorage.setItem('showConfirmationPopup', JSON.stringify(showConfirmationPopup));
  }, [showConfirmationPopup]);

  const clearSelectedPrice = () => {
    setSelectedPrice(null);
  };

  // Calculate margin requirement for a trade
  const calculateMarginRequirement = (price: number, amount: number, leverage: number): number => {
    const tradeValue = price * amount;
    const marginRequired = tradeValue / leverage;
    return marginRequired;
  };

  const executeOrder = async (details?: OrderDetails): Promise<{ success: boolean; error?: string }> => {
    const orderToExecute = details || orderDetails;
    if (!orderToExecute) {
      return { success: false, error: 'No order details' };
    }

    try {
      // Calculate margin requirement
      const marginRequired = calculateMarginRequirement(
        orderToExecute.price, 
        orderToExecute.amount, 
        orderToExecute.leverage
      );
      
      // Check if sufficient balance for margin requirement
      const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
      if (marginRequired > currentBalance) {
        const errorMessage = `Insufficient margin. Required: $${marginRequired.toFixed(2)}, Available: $${currentBalance.toFixed(2)}`;
        
        // Show error popup if TradingErrorContext is available
        if (showTradingError) {
          showTradingError({
            type: 'insufficient_balance',
            message: errorMessage,
            details: `You need $${(marginRequired - currentBalance).toFixed(2)} more to place this trade.`,
            orderDetails: {
              side: orderToExecute.side,
              type: orderToExecute.type,
              symbol: orderToExecute.symbol,
              amount: orderToExecute.amount,
              price: orderToExecute.price,
            }
          });
        }
        
        return { 
          success: false, 
          error: errorMessage 
        };
      }
      
      // Simulate order execution with potential errors
      // In a real implementation, this would call your trading API
      const executionResult = await simulateOrderExecution(orderToExecute);
      
      if (!executionResult.success) {
        const errorMessage = executionResult.error || 'Order execution failed. Please try again.';
        
        // Show error popup if TradingErrorContext is available
        if (showTradingError) {
          showTradingError({
            type: (executionResult.errorType as 'insufficient_balance' | 'order_failed' | 'network_error' | 'validation_error' | 'unknown') || 'order_failed',
            message: errorMessage,
            details: executionResult.details || 'This might be due to market conditions or system issues.',
            orderDetails: {
              side: orderToExecute.side,
              type: orderToExecute.type,
              symbol: orderToExecute.symbol,
              amount: orderToExecute.amount,
              price: orderToExecute.price,
            }
          });
        }
        
        return { 
          success: false, 
          error: errorMessage 
        };
      }
      
      // Handle different order types
      if (orderToExecute.type === 'market') {
        // Market orders execute immediately and go to open positions
        if (addOpenPosition) {
          addOpenPosition({
            symbol: orderToExecute.symbol,
            type: orderToExecute.side === 'buy' ? 'Buy' : 'Sell',
            size: orderToExecute.amount.toString(),
            entry: orderToExecute.price.toString(),
            current: orderToExecute.price.toString(),
            pnl: '$0.00',
            pnlPercent: '0.00%',
            leverage: orderToExecute.leverage,
            orderType: orderToExecute.type,
          });
          
          // Deduct margin from account balance for market orders
          if (updateBalance && accountBalance) {
            const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
            const newBalance = currentBalance - marginRequired;
            await updateBalance(accountType, newBalance);
          }
        }
      } else {
        // Limit and stop orders go to pending orders
        if (addPendingOrder) {
          addPendingOrder({
            symbol: orderToExecute.symbol,
            type: orderToExecute.side === 'buy' ? 'Buy' : 'Sell',
            size: orderToExecute.amount.toString(),
            price: orderToExecute.price.toString(),
            stopPrice: orderToExecute.stopPrice?.toString(),
            orderType: orderToExecute.type,
            status: 'Pending',
            leverage: orderToExecute.leverage,
          });
          
          // Deduct margin from account balance for pending orders
          if (updateBalance && accountBalance) {
            const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
            const newBalance = currentBalance - marginRequired;
            await updateBalance(accountType, newBalance);
          }
        }
      }
      
      // Clear the order details and close confirmation
      setOrderDetails(null);
      setShowOrderConfirmation(false);
      clearSelectedPrice();
      
      return { success: true }; // Order executed successfully
    } catch (error) {
      console.error('Error executing order:', error);
      
      const errorMessage = 'An unexpected error occurred while processing your order.';
      
      // Show error popup if TradingErrorContext is available
      if (showTradingError) {
        showTradingError({
          type: 'unknown',
          message: errorMessage,
          details: error instanceof Error ? error.message : 'Please try again later.',
          orderDetails: {
            side: orderToExecute.side,
            type: orderToExecute.type,
            symbol: orderToExecute.symbol,
            amount: orderToExecute.amount,
            price: orderToExecute.price,
          }
        });
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Simulate order execution with potential failures
  const simulateOrderExecution = async (orderDetails: OrderDetails): Promise<{ success: boolean; error?: string; errorType?: string; details?: string }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Simulate random failures (20% chance of failure for demo purposes)
    // In a real implementation, this would be replaced with actual API calls
    const randomFailure = Math.random() < 0.2; // 20% chance of failure
    
    if (randomFailure) {
      // Simulate different types of failures
      const failureType = Math.random();
      if (failureType < 0.2) {
        return {
          success: false,
          error: 'Network connection timeout',
          errorType: 'network_error',
          details: 'Unable to connect to the trading server. Please check your internet connection.'
        };
      } else if (failureType < 0.4) {
        return {
          success: false,
          error: 'Market is closed',
          errorType: 'order_failed',
          details: 'Trading is currently unavailable. Please try again during market hours.'
        };
      } else if (failureType < 0.6) {
        return {
          success: false,
          error: 'Order validation failed',
          errorType: 'validation_error',
          details: 'The order parameters are invalid. Please check your input values.'
        };
      } else if (failureType < 0.8) {
        return {
          success: false,
          error: 'Trading server error',
          errorType: 'order_failed',
          details: 'The trading server encountered an error. Please try again in a moment.'
        };
      } else {
        return {
          success: false,
          error: 'Order rejected by exchange',
          errorType: 'order_failed',
          details: 'The order was rejected by the exchange. This might be due to market conditions.'
        };
      }
    }
    
    return { success: true };
  };

  const value: OrderContextType = {
    selectedPrice,
    setSelectedPrice,
    clearSelectedPrice,
    showOrderConfirmation,
    setShowOrderConfirmation,
    orderDetails,
    setOrderDetails,
    executeOrder,
    showConfirmationPopup,
    setShowConfirmationPopup,
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};
